import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const PRODUCTS = ["Basmati Rice", "White Rice", "Brown Rice", "Parboiled Rice"];
const PACKAGING = ["25kg PP bags", "50kg PP bags", "1 MT jumbo bags", "Custom retail", "Bulk container"];
const INCOTERMS = ["FOB Karachi", "CIF (your port)", "CFR (your port)"];

export default function RFQ() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    contactPerson: "",
    email: "",
    phone: "",
    country: "",
    product: "",
    quantity: "",
    packaging: "",
    incoterm: "",
    destinationPort: "",
    message: "",
  });

  const handleChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // MOCK: In frontend-only mode, simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    toast.success("RFQ submitted successfully!", {
      description: "Our export team will respond within 24 hours.",
    });

    setSubmitted(true);
    setLoading(false);
  };

  const handleNewRFQ = () => {
    setSubmitted(false);
    setFormData({
      companyName: "",
      contactPerson: "",
      email: "",
      phone: "",
      country: "",
      product: "",
      quantity: "",
      packaging: "",
      incoterm: "",
      destinationPort: "",
      message: "",
    });
  };

  if (submitted) {
    return (
      <div data-testid="rfq-success" className="py-32">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <CheckCircle2 size={64} className="text-[#3FD97A] mx-auto" />
          <h1 className="mt-8 font-serif text-5xl">RFQ Submitted</h1>
          <p className="mt-6 text-foreground/75 text-lg leading-relaxed">
            Thank you for your request. Our export team has received your RFQ and will respond 
            with a detailed quotation, product specifications, and available shipping dates within 
            24 business hours.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={handleNewRFQ}
              data-testid="rfq-new"
              className="bg-[#3FD97A] hover:bg-[#4FE98A] text-[#0A0909] font-mono text-[11px] uppercase tracking-[0.22em] px-8 py-6 font-semibold"
            >
              Submit Another RFQ
            </Button>
            <a
              href="/"
              className="inline-flex items-center justify-center border border-border hover:border-[#3FD97A] px-8 py-6 font-mono text-[11px] uppercase tracking-[0.22em] hover-shift"
            >
              Back to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-rfq" className="py-16 md:py-20">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="overline">Request For Quotation</div>
        <h1 className="mt-4 font-serif text-5xl md:text-7xl tracking-tight max-w-4xl">
          Get a formal export quote.
        </h1>
        <p className="mt-8 text-foreground/75 text-lg max-w-3xl leading-relaxed">
          Fill in the form below with your requirements. Our team will respond within one business day 
          with pricing, lead times, and all necessary documentation.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-16 border border-border p-10 md:p-14 bg-[#0A0909]">
          {/* Company Information */}
          <div className="mb-10">
            <h2 className="font-serif text-3xl mb-6">Company Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="companyName" className="font-mono text-[11px] uppercase tracking-[0.2em]">
                  Company Name *
                </Label>
                <Input
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={(e) => handleChange("companyName", e.target.value)}
                  required
                  className="mt-2 bg-[#141212] border-border"
                  data-testid="rfq-company"
                />
              </div>
              <div>
                <Label htmlFor="contactPerson" className="font-mono text-[11px] uppercase tracking-[0.2em]">
                  Contact Person *
                </Label>
                <Input
                  id="contactPerson"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={(e) => handleChange("contactPerson", e.target.value)}
                  required
                  className="mt-2 bg-[#141212] border-border"
                  data-testid="rfq-contact"
                />
              </div>
              <div>
                <Label htmlFor="email" className="font-mono text-[11px] uppercase tracking-[0.2em]">
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  required
                  className="mt-2 bg-[#141212] border-border"
                  data-testid="rfq-email"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="font-mono text-[11px] uppercase tracking-[0.2em]">
                  Phone (with country code) *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  required
                  placeholder="+1 555 1234567"
                  className="mt-2 bg-[#141212] border-border"
                  data-testid="rfq-phone"
                />
              </div>
              <div>
                <Label htmlFor="country" className="font-mono text-[11px] uppercase tracking-[0.2em]">
                  Country *
                </Label>
                <Input
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={(e) => handleChange("country", e.target.value)}
                  required
                  className="mt-2 bg-[#141212] border-border"
                  data-testid="rfq-country"
                />
              </div>
            </div>
          </div>

          <div className="divider my-10"></div>

          {/* Product Requirements */}
          <div className="mb-10">
            <h2 className="font-serif text-3xl mb-6">Product Requirements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="product" className="font-mono text-[11px] uppercase tracking-[0.2em]">
                  Product *
                </Label>
                <Select
                  value={formData.product}
                  onValueChange={(value) => handleChange("product", value)}
                  required
                >
                  <SelectTrigger className="mt-2 bg-[#141212] border-border" data-testid="rfq-product">
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCTS.map((prod) => (
                      <SelectItem key={prod} value={prod}>
                        {prod}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="quantity" className="font-mono text-[11px] uppercase tracking-[0.2em]">
                  Quantity (in Metric Tons) *
                </Label>
                <Input
                  id="quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={(e) => handleChange("quantity", e.target.value)}
                  required
                  placeholder="e.g. 100"
                  className="mt-2 bg-[#141212] border-border"
                  data-testid="rfq-quantity"
                />
              </div>
              <div>
                <Label htmlFor="packaging" className="font-mono text-[11px] uppercase tracking-[0.2em]">
                  Packaging *
                </Label>
                <Select
                  value={formData.packaging}
                  onValueChange={(value) => handleChange("packaging", value)}
                  required
                >
                  <SelectTrigger className="mt-2 bg-[#141212] border-border" data-testid="rfq-packaging">
                    <SelectValue placeholder="Select packaging" />
                  </SelectTrigger>
                  <SelectContent>
                    {PACKAGING.map((pack) => (
                      <SelectItem key={pack} value={pack}>
                        {pack}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="incoterm" className="font-mono text-[11px] uppercase tracking-[0.2em]">
                  Incoterm *
                </Label>
                <Select
                  value={formData.incoterm}
                  onValueChange={(value) => handleChange("incoterm", value)}
                  required
                >
                  <SelectTrigger className="mt-2 bg-[#141212] border-border" data-testid="rfq-incoterm">
                    <SelectValue placeholder="Select incoterm" />
                  </SelectTrigger>
                  <SelectContent>
                    {INCOTERMS.map((term) => (
                      <SelectItem key={term} value={term}>
                        {term}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="destinationPort" className="font-mono text-[11px] uppercase tracking-[0.2em]">
                  Destination Port (if CIF/CFR)
                </Label>
                <Input
                  id="destinationPort"
                  name="destinationPort"
                  value={formData.destinationPort}
                  onChange={(e) => handleChange("destinationPort", e.target.value)}
                  placeholder="e.g. Port of Los Angeles, USA"
                  className="mt-2 bg-[#141212] border-border"
                  data-testid="rfq-port"
                />
              </div>
            </div>
          </div>

          <div className="divider my-10"></div>

          {/* Additional Information */}
          <div className="mb-10">
            <h2 className="font-serif text-3xl mb-6">Additional Information</h2>
            <div>
              <Label htmlFor="message" className="font-mono text-[11px] uppercase tracking-[0.2em]">
                Special Requirements / Message
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={(e) => handleChange("message", e.target.value)}
                rows={6}
                placeholder="Please specify any grade requirements, delivery timeline, certification needs, or other special requirements..."
                className="mt-2 bg-[#141212] border-border resize-none"
                data-testid="rfq-message"
              />
            </div>
          </div>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between pt-6 border-t border-border">
            <p className="text-sm text-foreground/60">
              * Required fields. We'll respond within 24 business hours.
            </p>
            <Button
              type="submit"
              disabled={loading}
              data-testid="rfq-submit"
              className="bg-[#3FD97A] hover:bg-[#4FE98A] text-[#0A0909] font-mono text-[11px] uppercase tracking-[0.22em] px-8 py-6 font-semibold"
            >
              {loading ? "Submitting..." : "Submit RFQ"}
            </Button>
          </div>
        </form>

        {/* Info Note */}
        <div className="mt-10 border-l-2 border-[#3FD97A] pl-6 py-4">
          <p className="text-sm text-foreground/75 leading-relaxed">
            <strong>What happens next?</strong> Our export team will review your requirements and prepare a detailed 
            quotation including unit price, lead time, shipping costs (if CIF/CFR), and all necessary export documentation. 
            Product samples and Certificates of Analysis can be arranged upon request.
          </p>
        </div>
      </div>
    </div>
  );
}
